import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Heart,
  Send,
  MessageSquareHeart,
  Clock,
  CheckCircle2,
  Gift,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface PendingContribution {
  id: string;
  walletId: string;
  type: string;
  amount: number;
  contributorName?: string;
  contributorEmail?: string;
  createdAt: string;
  walletPatientName: string;
}

interface ThankYouMessage {
  id: string;
  walletTransactionId: string;
  walletId: string;
  contributorName?: string;
  contributorEmail?: string;
  message: string;
  sentAt: string;
  createdAt: string;
  walletPatientName?: string;
}

function formatCents(cents: number): string {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`;
}

const quickMessages = [
  "Thank you so much for your generous contribution! It means the world to me and my family.",
  "Your support is making a real difference in my healthcare journey. I'm deeply grateful!",
  "I can't express how much your contribution helps. Thank you for being part of my support network!",
  "Your kindness and generosity inspire me. Thank you for helping me on my path to better health!",
];

export function ThankYouPrompt({ walletId }: { walletId?: string }) {
  const [selectedTx, setSelectedTx] = useState<PendingContribution | null>(null);
  const [thankYouMessage, setThankYouMessage] = useState("");
  const { toast } = useToast();

  const { data: pendingContributions = [], isLoading } = useQuery<PendingContribution[]>({
    queryKey: ["/api/thank-yous/pending"],
  });

  const sendThankYouMutation = useMutation({
    mutationFn: async ({ walletTransactionId, message }: { walletTransactionId: string; message: string }) => {
      const response = await apiRequest("POST", "/api/thank-yous", { walletTransactionId, message });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/thank-yous/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/thank-yous/pending/count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/thank-yous/sent"] });
      setSelectedTx(null);
      setThankYouMessage("");
      toast({
        title: "Thank-you sent!",
        description: "Your gratitude message has been delivered.",
      });
    },
    onError: (error: any) => {
      let errorMsg = "Could not send thank-you message. Please try again.";
      if (error.message) {
        try {
          const jsonPart = error.message.substring(error.message.indexOf("{"));
          const parsed = JSON.parse(jsonPart);
          if (parsed.error) errorMsg = parsed.error;
        } catch {
          errorMsg = error.message;
        }
      }
      toast({
        title: "Failed to send",
        description: errorMsg,
        variant: "destructive",
      });
    },
  });

  const filtered = walletId
    ? pendingContributions.filter(c => c.walletId === walletId)
    : pendingContributions;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20" />
        </CardContent>
      </Card>
    );
  }

  if (filtered.length === 0) return null;

  return (
    <>
      <Card className="border-primary/30 bg-primary/5" data-testid="section-pending-thank-yous">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquareHeart className="h-5 w-5 text-primary" />
            Send Thank-You Notes
            <Badge variant="secondary" data-testid="badge-pending-count">{filtered.length}</Badge>
          </CardTitle>
          <CardDescription>
            Show your gratitude to contributors who supported your healthcare fund.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filtered.slice(0, 5).map((contribution) => (
              <div
                key={contribution.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-background"
                data-testid={`pending-thank-you-${contribution.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-1.5 rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 shrink-0">
                    <Gift className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {contribution.contributorName || "Anonymous"}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <span className="font-medium text-green-600 dark:text-green-400">
                        +{formatCents(contribution.amount)}
                      </span>
                      <span>{format(new Date(contribution.createdAt), "MMM d")}</span>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedTx(contribution);
                    setThankYouMessage("");
                  }}
                  data-testid={`button-thank-${contribution.id}`}
                >
                  <Heart className="h-3.5 w-3.5 mr-1" />
                  Thank
                </Button>
              </div>
            ))}
            {filtered.length > 5 && (
              <p className="text-xs text-muted-foreground text-center">
                +{filtered.length - 5} more contributions awaiting your thanks
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedTx} onOpenChange={(open) => !open && setSelectedTx(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              Send Thank-You
            </DialogTitle>
            <DialogDescription>
              {selectedTx && (
                <>
                  Send a personalized thank-you to{" "}
                  <span className="font-medium text-foreground">
                    {selectedTx.contributorName || "your contributor"}
                  </span>{" "}
                  for their{" "}
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {formatCents(selectedTx.amount)}
                  </span>{" "}
                  contribution.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your message</label>
              <Textarea
                placeholder="Write a heartfelt thank-you message..."
                value={thankYouMessage}
                onChange={(e) => setThankYouMessage(e.target.value)}
                className="min-h-[100px] resize-none"
                data-testid="input-thank-you-message"
              />
              <p className="text-xs text-muted-foreground">
                {thankYouMessage.length}/500 characters
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Quick suggestions</p>
              <div className="space-y-1.5">
                {quickMessages.map((msg, i) => (
                  <button
                    key={i}
                    onClick={() => setThankYouMessage(msg)}
                    className="w-full text-left text-xs p-2 rounded-md border hover-elevate cursor-pointer transition-colors"
                    data-testid={`button-quick-message-${i}`}
                  >
                    {msg}
                  </button>
                ))}
              </div>
            </div>
            <Button
              className="w-full"
              disabled={!thankYouMessage.trim() || thankYouMessage.length > 500 || sendThankYouMutation.isPending}
              onClick={() => {
                if (selectedTx) {
                  sendThankYouMutation.mutate({
                    walletTransactionId: selectedTx.id,
                    message: thankYouMessage,
                  });
                }
              }}
              data-testid="button-send-thank-you"
            >
              {sendThankYouMutation.isPending ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Thank-You
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReceivedThankYous() {
  const { data: received = [], isLoading } = useQuery<ThankYouMessage[]>({
    queryKey: ["/api/thank-yous/received"],
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20" />
        </CardContent>
      </Card>
    );
  }

  if (received.length === 0) return null;

  return (
    <Card data-testid="section-received-thank-yous">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquareHeart className="h-5 w-5 text-primary" />
          Thank-You Messages
        </CardTitle>
        <CardDescription>Messages from people you've supported</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className={received.length > 3 ? "h-[250px]" : ""}>
          <div className="space-y-3">
            {received.map((msg) => (
              <div
                key={msg.id}
                className="p-3 rounded-lg border bg-primary/5"
                data-testid={`received-thank-you-${msg.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="p-1.5 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
                    <Heart className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {msg.walletPatientName && (
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        From {msg.walletPatientName}'s wallet
                      </p>
                    )}
                    <p className="text-sm italic text-foreground">"{msg.message}"</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground flex-wrap">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span>{format(new Date(msg.sentAt), "MMM d, yyyy")}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

export function SentThankYous() {
  const { data: sent = [], isLoading } = useQuery<ThankYouMessage[]>({
    queryKey: ["/api/thank-yous/sent"],
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20" />
        </CardContent>
      </Card>
    );
  }

  if (sent.length === 0) return null;

  return (
    <Card data-testid="section-sent-thank-yous">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Send className="h-5 w-5 text-muted-foreground" />
          Sent Thank-Yous
        </CardTitle>
        <CardDescription>Thank-you messages you've sent to contributors</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className={sent.length > 3 ? "h-[250px]" : ""}>
          <div className="space-y-3">
            {sent.map((msg) => (
              <div
                key={msg.id}
                className="flex items-start gap-3 p-3 rounded-lg border"
                data-testid={`sent-thank-you-${msg.id}`}
              >
                <div className="p-1.5 rounded-full bg-muted shrink-0 mt-0.5">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{msg.contributorName || "Contributor"}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">"{msg.message}"</p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground flex-wrap">
                    <Clock className="h-3 w-3" />
                    <span>Sent {format(new Date(msg.sentAt), "MMM d, yyyy")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
